import { useDarkMode } from "@/hooks/useDarkMode";
import { cn } from "@/lib/utils";
import { bucketUrlPrefix } from "common/config";
import { AccessibilityCategoryHybrid } from "common/types";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import SmoothCollapse from "react-smooth-collapse";
import AccessibilitySubItemHybrid from "./AccessibilitySubItemHybrid";
import FlagVenueAccessibility from "@/features/flag-report/FlagVenueAccessibility";
import { useAuth } from "common/hooks";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";

type AccessibilityItemHybridProps = {
  category: AccessibilityCategoryHybrid;
  fullSize?: boolean;
  venueId: string;
  venueName: string;
  businessId: string;
  onLogin: () => void;
  venueDetails: any;
};

function AccessibilityItemHybrid({
  category,
  fullSize,
  venueId,
  venueName,
  businessId,
  onLogin,
  venueDetails,
}: AccessibilityItemHybridProps) {
  const isDark = useDarkMode();
  const [isFlagVenueOpen, setIsFlagVenueOpen] = useState(false);
  const [selectedTagForFlag, setSelectedTagForFlag] = useState<any>(null);
  const { user, isLoggedIn } = useAuth();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "flagAccessibility") {
        setSelectedTagForFlag(actionToPerformValueAfterLogin);
        setIsFlagVenueOpen(true);
      }
      setActionToPerformAfterLogin("");
    }
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  return (
    <div className="">
      <div
        role="button"
        className="flex justify-between items-center mb-2 px-2 md:px-0 md:mr-2"
      >
        <div className="flex items-center gap-6">
          <img
            className="w-[24px] h-[24px]"
            src={
              bucketUrlPrefix +
              (isDark ? category.catImageDark : category.catImage)
            }
            alt=""
          />
          <h1 className="desktop-h3-small-semi-bold text-primary-800 dark:text-primary-150">
            {category.catName}
          </h1>
        </div>
      </div>
      <section className="px-2 py-4 md:px-0 md:mr-2">
        <div>
          {category.categoryOptions.map((categoryOption, categoryIndex) =>
            categoryOption.pageChildrenCategories.map(
              (pageChildrenCategory, childrenCategoryIndex) =>
                pageChildrenCategory.accessibilityOptions.map(
                  (accessibilityOption, accessibilityIndex) =>
                    accessibilityOption.levelOptions.map(
                      (levelOption, levelIndex) => (
                        <div
                          key={`${categoryIndex}-${childrenCategoryIndex}-${accessibilityIndex}-${levelIndex}`}
                          className="mb-8"
                        >
                          <AccessibilitySubItemHybrid
                            venueId={venueId}
                            pageChildrenCategoryName={pageChildrenCategory.name}
                            levelOption={levelOption}
                            fullSize={fullSize}
                            onFlag={(mainTag) => {
                              if (!user || !isLoggedIn) {
                                // Set the method in the Atom
                                setActionToPerformAfterLogin(
                                  "flagAccessibility"
                                );
                                setActionToPerformValueAfterLogin(mainTag);

                                onLogin();
                                return;
                              }
                              setSelectedTagForFlag(mainTag);
                              setIsFlagVenueOpen(true);
                            }}
                            lastUpdated={venueDetails.latestReviewDateUpdated}
                          />
                        </div>
                      )
                    )
                )
            )
          )}
        </div>
      </section>
      <FlagVenueAccessibility
        venueId={businessId}
        venueName={venueName}
        mainTag={selectedTagForFlag}
        catName={category.catName}
        isOpen={isFlagVenueOpen}
        onClose={() => setIsFlagVenueOpen(false)}
        venueDetails={venueDetails}
      />
    </div>
  );
}

export default AccessibilityItemHybrid;
